import { useMediaQuery } from '@uidotdev/usehooks';
import { AutoAwesome, PlusIcon, SearchIcon, StarOutlineOutlined, SvgIconComponent, TimerIcon } from 'components/icons';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { useFloatingAiAvailable } from 'pages/ai-chat/floating/FloatingAiAssistant';
import { KeyboardEvent, ReactNode, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'store';
import { openFloatingAiWithPrompt } from 'store/reducers/aiChat';
import { GLOBAL_SEARCH_NARROW_QUERY, GlobalSearchEntityType } from 'types/globalSearch';
import { searchResultsPath } from 'utils/globalSearchRoutes';
import { useHasPermissions } from 'utils/permissionUtil';
import { searchResultPin, usePins } from 'utils/pins';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { GLOBAL_SEARCH_ENTITY_LABEL_ID } from './entityDisplay';
import { MOD_ENTER_HINT, useGlobalSearchEntry } from './GlobalSearchBar';
import { GlobalSearchDropdown } from './GlobalSearchDropdown';
import { SearchCloseContext } from './GlobalSearchPeekCard';
import { GlobalSearchRow, isPlainLeftClick, PaletteContext } from './GlobalSearchRow';
import { GlobalSearchSection } from './GlobalSearchSection';
import { GlobalSearchSheet } from './GlobalSearchSheet';
import { splitHighlightRuns } from './HighlightedText';
import { useSearchKeyDown, useSharedSearchQuery } from './searchInput';
import { useGlobalSearchOptions } from './useGlobalSearchOptions';
import { useGlobalSearchSelection } from './useGlobalSearchSelection';

const LISTBOX_ID = 'global-search-dialog-listbox';

type Chip = GlobalSearchEntityType | 'ALL';
const CHIPS: Chip[] = ['ALL', 'MATTER', 'PARTY', 'INVOICE'];

const CREATE_TIMECARDS = ['CREATE.TIMECARDS'];
// The Cases list's New button asks for the same two.
const CREATE_MATTERS = ['CREATE.MATTERS', 'CREATE.MATTERS.CLIENTS'];

/** A row the dialog adds under the results: an action on the top hit, or the hand-off to Crespect AI. */
type ExtraRow = {
    id: string;
    icon: SvgIconComponent;
    title: ReactNode;
    href: string;
    trailing?: string;
    tone?: 'ai';
    select: () => void;
};

/** A result's text without its highlight sentinels: what a pin stores and what an action names. */
const plainText = (text: string) =>
    splitHighlightRuns(text)
        .map((run) => run.text)
        .join('');

/**
 * Global search as the new design's ⌘K dialog: the header's typeahead list in one centred card, with
 * chips narrowing it to one family, actions on the top hit, a row asking Crespect AI and the keys in its
 * footer. Below the breakpoint it is the same list in today's sheet.
 *
 * It registers the shortcut and `focusGlobalSearch` itself, since the new design has no header bar to.
 */
export function GlobalSearchDialog() {
    const intl = useIntl();
    const dispatch = useDispatch();
    const navigateWithReturn = useNavigateWithReturnState();
    const narrow = useMediaQuery(GLOBAL_SEARCH_NARROW_QUERY);
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [chip, setChip] = useState<Chip>('ALL');
    const [query, setQuery] = useSharedSearchQuery(open);
    const selection = useGlobalSearchSelection(query);
    const canLogTime = useHasPermissions(CREATE_TIMECARDS);
    const canCreateCase = useHasPermissions(CREATE_MATTERS);
    const canAskAi = useFloatingAiAvailable();
    const { isPinned, setPinned } = usePins();

    const changeOpen = (next: boolean) => {
        setOpen(next);
        if (!next) {
            selection.forget();
            setChip('ALL');
        }
    };
    const close = () => changeOpen(false);

    // With the dialog already up, the shortcut selects what is typed, as it does in the header's box.
    const openSearch = useCallback(() => {
        setOpen(true);
        inputRef.current?.select();
    }, []);
    useGlobalSearchEntry(openSearch);

    const model = useGlobalSearchOptions({
        query,
        listboxId: LISTBOX_ID,
        surface: narrow ? 'sheet' : 'dropdown',
        open,
        close,
        entityType: chip === 'ALL' ? undefined : chip,
    });

    const optionId = (suffix: string) => `${LISTBOX_ID}-${suffix}`;
    const openPage = (href: string) => {
        close();
        navigateWithReturn(href);
    };

    // The actions hang under the results, so they show only where the list draws some. They follow from
    // the top hit's family and id alone: nothing waits for its peek.
    const drawsResults = !model.showRecents && model.options.length > 0;
    const topHit = drawsResults ? (model.bestBet?.row ?? model.sectionRows[0]?.rows[0]?.row) : undefined;
    const topHitName = topHit ? plainText(topHit.displayName) : '';
    const named = (id: string) =>
        intl.formatMessage(
            { id },
            {
                name: (
                    <strong key="name" className="font-semibold">
                        {topHitName}
                    </strong>
                ),
            }
        );
    const actions: ExtraRow[] = [];
    if (topHit?.entityType === 'MATTER' && canLogTime) {
        const href = `/matter/${topHit.id}/timecards/new`;
        actions.push({
            id: optionId('log-time'),
            icon: TimerIcon,
            title: named('search.log-time-on'),
            href,
            select: () => openPage(href),
        });
    }
    if (topHit?.entityType === 'PARTY' && canCreateCase) {
        const href = `/contact/${topHit.id}/matter/new`;
        actions.push({
            id: optionId('new-case'),
            icon: PlusIcon,
            title: named('search.new-case-for'),
            href,
            select: () => openPage(href),
        });
    }
    // Pinned under the key its record header's star uses.
    const pin = topHit ? searchResultPin(topHit.entityType, topHit.id, topHitName) : undefined;
    if (pin && !isPinned(pin.key)) {
        actions.push({
            id: optionId('pin'),
            icon: StarOutlineOutlined,
            title: named('search.pin'),
            href: pin.url,
            select: () => {
                setPinned(pin, true);
                close();
            },
        });
    }

    // The query goes to the panel's composer, for the user to send.
    const askAi =
        canAskAi && model.trimmed !== ''
            ? () => {
                  close();
                  dispatch(openFloatingAiWithPrompt(model.trimmed));
              }
            : undefined;
    // Under results, and under a search that failed or found nothing, where it is the way forward.
    const askRow: ExtraRow | undefined =
        askAi && !model.showRecents && (drawsResults || !model.isLoading)
            ? {
                  id: optionId('ask-ai'),
                  icon: AutoAwesome,
                  title: model.trimmed,
                  // Where a new tab can take the question: the AI page.
                  href: '/ai-chat',
                  trailing: MOD_ENTER_HINT,
                  tone: 'ai',
                  select: askAi,
              }
            : undefined;

    const keys = selection.keysFor([...model.options, ...actions, ...(askRow ? [askRow] : [])], model.firstId);
    const searchKeyDown = useSearchKeyDown({
        query,
        close,
        onArrowDown: keys.onArrowDown,
        onArrowUp: keys.onArrowUp,
        onEnter: keys.onEnter,
    });
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && askAi) {
            event.preventDefault();
            askAi();
            return;
        }
        searchKeyDown(event);
    };

    const extraRow = (row: ExtraRow) => (
        <GlobalSearchRow
            key={row.id}
            id={row.id}
            icon={row.icon}
            title={row.title}
            trailing={row.trailing}
            tone={row.tone}
            href={row.href}
            selected={row.id === keys.activeOptionId}
            onSelect={row.select}
        />
    );

    const total = model.data?.capped ? `${model.data.total}+` : (model.data?.total ?? 0);
    const footer = (
        <div className="border-neutral-tint bg-surface-tile text-grey-600 flex h-10 flex-none items-center gap-4 border-t px-[18px] text-xs">
            <span className="truncate">{intl.formatMessage({ id: 'global-search.keyboard-hint' })}</span>
            <a
                href={searchResultsPath(model.trimmed)}
                onClick={(event) => {
                    if (!isPlainLeftClick(event)) {
                        return;
                    }
                    event.preventDefault();
                    model.openResultsPage();
                }}
                className="text-primary ml-auto flex-none font-medium no-underline hover:underline"
            >
                {intl.formatMessage({ id: 'global-search.see-all-total' }, { total })}
            </a>
        </div>
    );

    const panel = (
        <SearchCloseContext.Provider value={close}>
            <GlobalSearchDropdown
                model={model}
                listboxId={LISTBOX_ID}
                activeOptionId={keys.activeOptionId}
                variant={narrow ? 'sheet' : 'dropdown'}
                onDidYouMean={setQuery}
                offersAskAi={askAi !== undefined}
                footer={narrow ? undefined : footer}
            >
                {actions.length > 0 || askRow ? (
                    <>
                        {actions.length > 0 && (
                            <GlobalSearchSection title={intl.formatMessage({ id: 'actions' })} divided>
                                {actions.map(extraRow)}
                            </GlobalSearchSection>
                        )}
                        {askRow && (
                            <GlobalSearchSection title={intl.formatMessage({ id: 'menu.ask-ai' })} divided>
                                {extraRow(askRow)}
                            </GlobalSearchSection>
                        )}
                    </>
                ) : undefined}
            </GlobalSearchDropdown>
        </SearchCloseContext.Provider>
    );

    if (narrow) {
        return (
            <GlobalSearchSheet
                open={open}
                onOpenChange={changeOpen}
                listboxId={LISTBOX_ID}
                query={query}
                onQueryChange={setQuery}
                {...keys}
                panel={panel}
            />
        );
    }

    const placeholder = intl.formatMessage({ id: 'global-search.placeholder' });

    return (
        <Dialog open={open} onOpenChange={changeOpen}>
            {/* One card, the field over today's panel, held near the top rather than centred so the field stays
                put while the list grows and shrinks. The panel's own corners and shadow are the card's here. */}
            <DialogContent
                aria-label={intl.formatMessage({ id: 'menu.search-or-jump' })}
                showCloseButton={false}
                initialFocus={inputRef}
                className="top-24 flex translate-y-0 flex-col gap-0 overflow-hidden p-0 ring-0 *:rounded-none *:shadow-none sm:max-w-180"
            >
                <div className="border-neutral-tint flex h-15 flex-none items-center gap-3 border-b px-[18px]">
                    <SearchIcon aria-hidden className="text-grey-600 size-[18px] flex-none" />
                    <input
                        ref={inputRef}
                        type="text"
                        role="combobox"
                        aria-expanded
                        aria-controls={LISTBOX_ID}
                        aria-activedescendant={keys.activeOptionId}
                        aria-autocomplete="list"
                        aria-label={placeholder}
                        placeholder={placeholder}
                        autoComplete="off"
                        spellCheck={false}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={handleKeyDown}
                        className="text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-lg outline-none"
                    />
                    <ToggleGroup
                        value={[chip]}
                        onValueChange={(value: string[]) => {
                            if (value.length > 0) {
                                setChip(value[0] as Chip);
                                // The keys work on the field, so it takes the focus back from the chip.
                                inputRef.current?.focus();
                            }
                        }}
                        spacing={1}
                    >
                        {CHIPS.map((value) => (
                            <ToggleGroupItem
                                key={value}
                                value={value}
                                size="sm"
                                className="bg-segmented text-foreground-secondary data-pressed:bg-primary-strong data-pressed:text-primary-foreground data-pressed:hover:bg-primary-strong data-pressed:hover:text-primary-foreground h-[26px] min-w-0 rounded-[6px] px-2 text-xs"
                            >
                                {intl.formatMessage({
                                    id: value === 'ALL' ? 'all' : GLOBAL_SEARCH_ENTITY_LABEL_ID[value],
                                })}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
                <PaletteContext.Provider value>{panel}</PaletteContext.Provider>
            </DialogContent>
        </Dialog>
    );
}
